import { API_BASE_URL } from '../config/env';
import {
  isAuthSessionError,
  notifySessionExpired,
} from '../utils/authSession';
import { clearGetCache } from './api';

function getAuthHeaders() {
  try {
    const token = window.localStorage.getItem('chse_auth_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

function buildQueryString(params = {}) {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.set(key, String(value));
    }
  });

  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

function getErrorMessage(data, status) {
  if (Array.isArray(data?.errors) && data.errors.length) {
    return data.errors.join(', ');
  }
  if (data?.message) {
    return data.message;
  }
  return `API error: ${status}`;
}

function invalidateOfflineCache() {
  clearGetCache('/api/vdocipher/offline_videos');
  clearGetCache('/api/vdocipher/offline_courses');
}

async function vdocipherRequest(endpoint, { method = 'GET', body } = {}) {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        accept: 'application/json',
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...getAuthHeaders(),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    let data = null;
    if (response.status !== 204) {
      try {
        data = await response.json();
      } catch {
        data = null;
      }
    }

    const message = getErrorMessage(data, response.status);
    const failed = !response.ok || data?.success === false;

    if (failed) {
      if (isAuthSessionError(response.status, message)) {
        notifySessionExpired();
      }

      return {
        success: false,
        message,
        data: data?.data ?? null,
      };
    }

    return data;
  } catch (error) {
    return {
      success: false,
      message: error?.message || 'Request failed',
      data: null,
    };
  }
}

const lessonOtpInflight = new Map();
const lessonOtpCache = new Map();
const LESSON_OTP_SUCCESS_TTL_MS = 5 * 60 * 1000;
const LESSON_OTP_ERROR_TTL_MS = 1500;

function readLessonOtpCache(lessonId) {
  const key = String(lessonId);
  const entry = lessonOtpCache.get(key);
  if (!entry) return null;

  const ttl = entry.success ? LESSON_OTP_SUCCESS_TTL_MS : LESSON_OTP_ERROR_TTL_MS;
  if (Date.now() - entry.at > ttl) {
    lessonOtpCache.delete(key);
    return null;
  }

  return entry.data;
}

function writeLessonOtpCache(lessonId, data) {
  lessonOtpCache.set(String(lessonId), {
    at: Date.now(),
    success: data?.success !== false,
    data,
  });
}

export function clearLessonOtpCache(lessonId) {
  if (lessonId == null) {
    lessonOtpCache.clear();
    lessonOtpInflight.clear();
    return;
  }

  const key = String(lessonId);
  lessonOtpCache.delete(key);
  lessonOtpInflight.delete(key);
}

const courseOtpInflight = new Map();
const courseOtpCache = new Map();
const COURSE_OTP_SUCCESS_TTL_MS = 5 * 60 * 1000;
const COURSE_OTP_ERROR_TTL_MS = 1500;

function readCourseOtpCache(courseId) {
  const key = String(courseId);
  const entry = courseOtpCache.get(key);
  if (!entry) return null;

  const ttl = entry.success ? COURSE_OTP_SUCCESS_TTL_MS : COURSE_OTP_ERROR_TTL_MS;
  if (Date.now() - entry.at > ttl) {
    courseOtpCache.delete(key);
    return null;
  }

  return entry.data;
}

function writeCourseOtpCache(courseId, data) {
  courseOtpCache.set(String(courseId), {
    at: Date.now(),
    success: data?.success !== false,
    data,
  });
}

export function clearCourseOtpCache(courseId) {
  if (courseId == null) {
    courseOtpCache.clear();
    courseOtpInflight.clear();
    return;
  }

  const key = String(courseId);
  courseOtpCache.delete(key);
  courseOtpInflight.delete(key);
}

export async function getCourseVdoCipherOtp(courseId) {
  const key = String(courseId);
  const cached = readCourseOtpCache(courseId);
  if (cached) return cached;

  if (courseOtpInflight.has(key)) {
    return courseOtpInflight.get(key);
  }

  const request = vdocipherRequest(`/api/courses/${courseId}/vdocipher_otp`)
    .then((result) => {
      writeCourseOtpCache(courseId, result);
      return result;
    })
    .finally(() => {
      courseOtpInflight.delete(key);
    });

  courseOtpInflight.set(key, request);
  return request;
}

export async function getLessonVdoCipherOtp(lessonId) {
  const key = String(lessonId);
  const cached = readLessonOtpCache(lessonId);
  if (cached) return cached;

  if (lessonOtpInflight.has(key)) {
    return lessonOtpInflight.get(key);
  }

  const request = vdocipherRequest(`/api/lessons/${lessonId}/vdocipher_otp`)
    .then((result) => {
      writeLessonOtpCache(lessonId, result);
      return result;
    })
    .finally(() => {
      lessonOtpInflight.delete(key);
    });

  lessonOtpInflight.set(key, request);
  return request;
}

export async function getLessonVdoCipherOfflineOtp(lessonId) {
  return vdocipherRequest(`/api/lessons/${lessonId}/vdocipher_offline_otp`);
}

export async function getOfflineVideos(params = {}) {
  return vdocipherRequest(`/api/vdocipher/offline_videos${buildQueryString(params)}`);
}

export async function removeOfflineVideos(payload = {}) {
  const response = await vdocipherRequest('/api/vdocipher/offline_videos', {
    method: 'DELETE',
    body: payload,
  });
  invalidateOfflineCache();
  return response;
}

export async function getOfflineCourses() {
  return vdocipherRequest('/api/vdocipher/offline_courses');
}

export async function removeOfflineCourse(courseId) {
  const response = await vdocipherRequest(`/api/vdocipher/offline_courses/${courseId}`, {
    method: 'DELETE',
  });
  invalidateOfflineCache();
  return response;
}

export async function getVdoCipherUserAnalytics() {
  return vdocipherRequest('/api/vdocipher/user_analytics');
}
